<script setup>
import { EMAIL_DO_SUPORTE } from 'dashboard/helper/suporteDoAdvup';
// INTEGRAÇÕES › TELEFONIA — fase 1 da telefonia nativa (25/09/2026).
//
// O administrador do escritório cria os ramais, vincula a quem usa e vê quem
// está online AGORA (o registro do aparelho na central, lido a cada 10 s).
// A central (PBX) é cadastrada pelo Super Admin; aqui ela só aparece com a
// saúde e o botão de testar.
//
// Cada ramal é um cartão com o número em algarismo de mostrador — é o que a
// equipe decora e disca — e a luz do estado: teal respirando quando online.
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { onClickOutside } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import TelefoniaAPI from 'dashboard/api/telefonia';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import EstadoVazio from 'dashboard/components-next/EstadoVazio.vue';
import EsqueletoLista from 'dashboard/components-next/EsqueletoLista.vue';
import AnimatedNumber from 'dashboard/components-next/AnimatedNumber.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import RamalModal from './RamalModal.vue';
import CredenciaisModal from './CredenciaisModal.vue';
import OperadoraSecao from './OperadoraSecao.vue';
import RoteamentoSecao from './roteamento/RoteamentoSecao.vue';
import {
  proximoNumero,
  estadoDoRamal,
  aparelhoLegivel,
} from 'dashboard/helper/telefonia';
import { iniciaisDoNome, corDoNome } from 'dashboard/helper/avatar';
import { dynamicTime } from 'shared/helpers/timeHelper';

const { t } = useI18n();
const T = 'TELEFONIA';

const carregando = ref(true);
const falha = ref(''); // '', 'geral', 'nao_contratada'
const servidor = ref({ cadastrado: false });
const ramais = ref([]);
const usuarios = ref([]);
const operadoras = ref([]);
const roteamento = ref({ filas: [], uras: [], horarios: [] });
const busca = ref('');
const atualizadoEm = ref(0);

// ── Carga e atualização ao vivo ────────────────────────────────────────────
async function carregar({ silencioso = false } = {}) {
  if (!silencioso) carregando.value = true;
  try {
    const { data } = await TelefoniaAPI.painel();
    servidor.value = data.servidor;
    ramais.value = data.ramais;
    usuarios.value = data.usuarios;
    operadoras.value = data.operadoras || [];
    if (data.roteamento) roteamento.value = data.roteamento;
    falha.value = '';
    atualizadoEm.value = Math.floor(Date.now() / 1000);
  } catch (e) {
    if (!silencioso)
      falha.value = e?.response?.status === 403 ? 'nao_contratada' : 'geral';
  } finally {
    carregando.value = false;
  }
}

// Quem está online muda sozinho (a pessoa abre o app no celular): 10 s basta,
// e só com a aba à vista.
let relogio = null;
onMounted(() => {
  carregar();
  relogio = setInterval(() => {
    if (!document.hidden && servidor.value.cadastrado)
      carregar({ silencioso: true });
  }, 10000);
});
onBeforeUnmount(() => clearInterval(relogio));

// ── A central ──────────────────────────────────────────────────────────────
const saude = computed(() => {
  const s = servidor.value;
  if (!s.ativo) return 'desligado';
  if (s.saude === 'ok') return 'ok';
  if (s.saude === 'erro') return 'erro';
  return 'nunca';
});
const ROTULO_DA_SAUDE = {
  ok: 'OK',
  erro: 'ERRO',
  nunca: 'NUNCA',
  desligado: 'DESLIGADO',
};
const testando = ref(false);
async function testar() {
  testando.value = true;
  try {
    const { data } = await TelefoniaAPI.verificar();
    servidor.value = { ...servidor.value, ...data };
    useAlert(
      data.saude === 'ok' ? t(`${T}.PBX.TESTE_OK`) : t(`${T}.PBX.TESTE_ERRO`)
    );
  } catch (e) {
    useAlert(e?.response?.data?.error || t(`${T}.PBX.TESTE_ERRO`));
  } finally {
    testando.value = false;
  }
}

// ── Números ────────────────────────────────────────────────────────────────
const ligados = computed(() => ramais.value.filter(r => r.ativo));
const online = computed(() => ramais.value.filter(r => r.ativo && r.online));
const gravam = computed(() => ligados.value.filter(r => r.gravar));
const pctOnline = computed(() =>
  ligados.value.length
    ? Math.round((online.value.length / ligados.value.length) * 100)
    : 0
);

// ── Lista ──────────────────────────────────────────────────────────────────
const visiveis = computed(() => {
  const termo = busca.value.trim().toLowerCase();
  if (!termo) return ramais.value;
  return ramais.value.filter(r =>
    [r.numero, r.nome, r.usuario?.nome].some(v =>
      (v || '').toLowerCase().includes(termo)
    )
  );
});
const ROTULO_DO_ESTADO = {
  online: 'ONLINE',
  offline: 'OFFLINE',
  desligado: 'DESLIGADO',
  erro: 'ERRO',
};
const legendaDo = r => {
  const estado = estadoDoRamal(r);
  const base = t(`${T}.LISTA.${ROTULO_DO_ESTADO[estado]}`);
  const aparelho = aparelhoLegivel(r.aparelho);
  return estado === 'online' && aparelho ? `${base} · ${aparelho}` : base;
};

// Menu de ações: um aberto por vez.
const menuAberto = ref(null);
const raizDoMenu = ref(null);
onClickOutside(raizDoMenu, () => {
  menuAberto.value = null;
});

// ── Criar / editar ─────────────────────────────────────────────────────────
const modal = ref(null); // null | 'novo' | ramal
const salvando = ref(false);
const erroDoModal = ref('');
const credenciais = ref(null); // { credenciais, ramal }

function abrirNovo() {
  erroDoModal.value = '';
  modal.value = 'novo';
}
function abrirEdicao(r) {
  menuAberto.value = null;
  erroDoModal.value = '';
  modal.value = r;
}

function trocar(atualizado) {
  const i = ramais.value.findIndex(r => r.id === atualizado.id);
  if (i >= 0) ramais.value.splice(i, 1, atualizado);
  else ramais.value.push(atualizado);
}

async function salvar(dados) {
  salvando.value = true;
  erroDoModal.value = '';
  try {
    if (modal.value === 'novo') {
      const { data } = await TelefoniaAPI.criarRamal(dados);
      trocar(data.ramal);
      credenciais.value = { credenciais: data.credenciais, ramal: data.ramal };
      useAlert(t(`${T}.MODAL.CRIADO`, { numero: data.ramal.numero }));
    } else {
      const { data } = await TelefoniaAPI.atualizarRamal(modal.value.id, dados);
      trocar(data.ramal);
      useAlert(t(`${T}.MODAL.SALVO`, { numero: data.ramal.numero }));
    }
    modal.value = null;
  } catch (e) {
    erroDoModal.value = e?.response?.data?.error || t(`${T}.ERRO.CARREGAR_TIT`);
  } finally {
    salvando.value = false;
  }
}

async function alternar(r) {
  menuAberto.value = null;
  const { data } = await TelefoniaAPI.atualizarRamal(r.id, {
    enabled: !r.ativo,
  });
  trocar(data.ramal);
}

async function reenviar(r) {
  try {
    const { data } = await TelefoniaAPI.reenviar(r.id);
    trocar(data.ramal);
  } catch (e) {
    useAlert(e?.response?.data?.error || t(`${T}.ERRO.CARREGAR_TIT`));
  }
}

// ── Confirmações ───────────────────────────────────────────────────────────
const confirmarApagar = ref(null);
const confirmarSenha = ref(null);
const dialogoApagar = ref(null);
const dialogoSenha = ref(null);
const ocupado = ref(false);

function pedirApagar(r) {
  menuAberto.value = null;
  confirmarApagar.value = r;
  dialogoApagar.value?.open();
}
function pedirSenha(r) {
  menuAberto.value = null;
  confirmarSenha.value = r;
  dialogoSenha.value?.open();
}
async function apagar() {
  ocupado.value = true;
  try {
    await TelefoniaAPI.apagarRamal(confirmarApagar.value.id);
    ramais.value = ramais.value.filter(r => r.id !== confirmarApagar.value.id);
    useAlert(t(`${T}.MODAL.APAGADO`));
    dialogoApagar.value?.close();
  } catch (e) {
    useAlert(e?.response?.data?.error || t(`${T}.ERRO.CARREGAR_TIT`));
  } finally {
    ocupado.value = false;
  }
}
async function gerarSenha() {
  ocupado.value = true;
  try {
    const { data } = await TelefoniaAPI.novaSenha(confirmarSenha.value.id);
    trocar(data.ramal);
    dialogoSenha.value?.close();
    credenciais.value = { credenciais: data.credenciais, ramal: data.ramal };
  } catch (e) {
    useAlert(e?.response?.data?.error || t(`${T}.ERRO.CARREGAR_TIT`));
  } finally {
    ocupado.value = false;
  }
}
</script>

<template>
  <div class="tel">
    <BaseSettingsHeader
      icone="i-ph-phone-fill"
      :title="t(`${T}.TITULO`)"
      :description="t(`${T}.DESCRICAO`)"
      :back-button-label="t(`${T}.VOLTAR`)"
    />

    <EsqueletoLista v-if="carregando" :linhas="4" :altura="96" />

    <EstadoVazio
      v-else-if="falha === 'nao_contratada'"
      icone="i-ph-phone-x"
      :titulo="t(`${T}.ERRO.NAO_CONTRATADA_TIT`)"
      :dica="t(`${T}.ERRO.NAO_CONTRATADA_DICA`, { email: EMAIL_DO_SUPORTE })"
    />
    <EstadoVazio
      v-else-if="falha"
      tom="erro"
      icone="i-ph-warning-circle"
      :titulo="t(`${T}.ERRO.CARREGAR_TIT`)"
      :dica="t(`${T}.ERRO.CARREGAR_DICA`)"
    >
      <button type="button" class="advup-btn" @click="carregar()">
        <i class="i-hugeicons-refresh" aria-hidden="true" />
        {{ t(`${T}.ERRO.TENTAR`) }}
      </button>
    </EstadoVazio>
    <EstadoVazio
      v-else-if="!servidor.cadastrado || !servidor.banco_configurado"
      icone="i-ph-hard-drives"
      :titulo="t(`${T}.ERRO.SEM_PBX_TIT`)"
      :dica="t(`${T}.ERRO.SEM_PBX_DICA`, { email: EMAIL_DO_SUPORTE })"
    />

    <template v-else>
      <!-- ── A central ───────────────────────────────────────────────── -->
      <section class="advup-card tel-central" :class="`tel-central--${saude}`">
        <span class="tel-central__orbe" aria-hidden="true">
          <i class="i-ph-phone-call-fill" />
        </span>
        <div class="tel-central__txt">
          <p class="tel-central__rot">{{ t(`${T}.PBX.TIT`) }}</p>
          <p class="tel-central__tit">
            {{ t(`${T}.PBX.${ROTULO_DA_SAUDE[saude]}`) }}
          </p>
          <p class="tel-central__sub">
            <span>{{
              t(`${T}.PBX.ONDE`, {
                dominio: servidor.dominio,
                porta: servidor.porta,
              })
            }}</span>
            <span v-if="servidor.verificado_em">
              {{
                t(`${T}.PBX.VISTO`, {
                  quando: dynamicTime(
                    new Date(servidor.verificado_em).getTime() / 1000
                  ),
                })
              }}
            </span>
          </p>
          <p
            v-if="saude === 'erro' && servidor.ultimo_erro"
            class="tel-central__erro"
          >
            {{ servidor.ultimo_erro }}
          </p>
        </div>
        <button
          type="button"
          class="advup-btn tel-central__testar"
          :disabled="testando"
          @click="testar"
        >
          <i
            :class="
              testando
                ? 'i-ph-circle-notch advup-gira'
                : 'i-hugeicons-plug-socket'
            "
            aria-hidden="true"
          />
          {{ testando ? t(`${T}.PBX.TESTANDO`) : t(`${T}.PBX.TESTAR`) }}
        </button>
      </section>

      <!-- ── Os números ──────────────────────────────────────────────── -->
      <div class="tel-numeros">
        <article class="kpi-card kpi-card--azul advup-card advup-entra">
          <span class="kpi-card__faixa" aria-hidden="true" />
          <span class="kpi-card__ico" aria-hidden="true">
            <i class="i-ph-phone-fill" />
          </span>
          <div class="min-w-0">
            <p class="kpi-card__valor">
              <AnimatedNumber :value="ramais.length" />
            </p>
            <p class="kpi-card__rot">{{ t(`${T}.NUMEROS.RAMAIS`) }}</p>
          </div>
        </article>
        <article
          class="kpi-card kpi-card--teal advup-card advup-entra tel-numeros__online"
          :class="{ 'kpi-card--zerado': !online.length }"
        >
          <span class="kpi-card__faixa" aria-hidden="true" />
          <span class="kpi-card__ico" aria-hidden="true">
            <i class="i-ph-broadcast-fill" />
          </span>
          <div class="min-w-0 flex-1">
            <p class="kpi-card__valor">
              <AnimatedNumber :value="online.length" />
            </p>
            <p class="kpi-card__rot">{{ t(`${T}.NUMEROS.ONLINE`) }}</p>
            <div class="kpi-card__barra">
              <p class="kpi-card__barra-rot">
                <span>{{ t(`${T}.NUMEROS.DE`, { n: ligados.length }) }}</span>
                <span class="kpi-card__barra-pct">{{ pctOnline }}%</span>
              </p>
              <span class="kpi-card__barra-trilho" aria-hidden="true">
                <span
                  class="kpi-card__barra-fio"
                  :style="{ '--pct': pctOnline }"
                />
              </span>
            </div>
          </div>
        </article>
        <article
          class="kpi-card kpi-card--neutro advup-card advup-entra tel-numeros__grava"
          :class="{ 'kpi-card--zerado': !gravam.length }"
        >
          <span class="kpi-card__faixa" aria-hidden="true" />
          <span class="kpi-card__ico" aria-hidden="true">
            <i class="i-ph-record-fill" />
          </span>
          <div class="min-w-0">
            <p class="kpi-card__valor">
              <AnimatedNumber :value="gravam.length" />
            </p>
            <p class="kpi-card__rot">{{ t(`${T}.NUMEROS.GRAVANDO`) }}</p>
          </div>
        </article>
      </div>

      <!-- ── Os ramais ───────────────────────────────────────────────── -->
      <section class="tel-ramais">
        <header class="tel-ramais__cab">
          <h2 class="tel-ramais__tit">
            {{ t(`${T}.LISTA.TIT`) }}
            <span class="tel-ramais__conta">{{ ramais.length }}</span>
          </h2>
          <div class="tel-ramais__ferramentas">
            <div
              v-if="ramais.length"
              class="advup-busca advup-busca--curta tel-ramais__busca"
            >
              <i
                class="advup-busca__lupa i-ph-magnifying-glass"
                aria-hidden="true"
              />
              <input
                v-model="busca"
                type="text"
                class="advup-busca__campo reset-base"
                :aria-label="t(`${T}.LISTA.BUSCA`)"
                :placeholder="t(`${T}.LISTA.BUSCA`)"
              />
            </div>
            <button type="button" class="advup-btn-forte" @click="abrirNovo">
              <i class="i-ph-phone-plus-fill" aria-hidden="true" />
              {{ t(`${T}.LISTA.NOVO`) }}
            </button>
          </div>
        </header>

        <EstadoVazio
          v-if="!ramais.length"
          class="advup-card"
          icone="i-ph-phone-plus"
          :titulo="t(`${T}.LISTA.VAZIO_TIT`)"
          :dica="t(`${T}.LISTA.VAZIO_DICA`)"
        >
          <button type="button" class="advup-btn-forte" @click="abrirNovo">
            <i class="i-ph-phone-plus-fill" aria-hidden="true" />
            {{ t(`${T}.LISTA.NOVO`) }}
          </button>
        </EstadoVazio>
        <EstadoVazio
          v-else-if="!visiveis.length"
          class="advup-card"
          icone="i-ph-funnel-simple"
          :titulo="t(`${T}.LISTA.VAZIO_BUSCA`)"
        />

        <TransitionGroup
          v-else
          ref="raizDoMenu"
          tag="ul"
          name="tel-card"
          class="tel-grade"
        >
          <li
            v-for="(r, i) in visiveis"
            :key="r.id"
            class="advup-card advup-card--flutua tel-card"
            :class="`tel-card--${estadoDoRamal(r)}`"
            :style="{ '--i': i }"
          >
            <div class="tel-card__topo">
              <span class="tel-card__numero">{{ r.numero }}</span>
              <span class="tel-card__estado">
                <span class="tel-card__luz" aria-hidden="true" />
                {{ legendaDo(r) }}
              </span>
              <div class="tel-card__menu">
                <button
                  v-tooltip="t(`${T}.LISTA.ACOES`, { numero: r.numero })"
                  type="button"
                  class="advup-acao advup-acao--p"
                  :aria-label="t(`${T}.LISTA.ACOES`, { numero: r.numero })"
                  aria-haspopup="menu"
                  :aria-expanded="menuAberto === r.id"
                  @click="menuAberto = menuAberto === r.id ? null : r.id"
                >
                  <i class="i-hugeicons-more-vertical" aria-hidden="true" />
                </button>
                <Transition name="tel-pop">
                  <div
                    v-if="menuAberto === r.id"
                    class="advup-menu advup-pop tel-card__opcoes"
                    role="menu"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      class="advup-menu__item"
                      @click="abrirEdicao(r)"
                    >
                      <i class="i-hugeicons-edit-02" aria-hidden="true" />{{
                        t(`${T}.LISTA.EDITAR`)
                      }}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      class="advup-menu__item"
                      @click="pedirSenha(r)"
                    >
                      <i class="i-hugeicons-key-01" aria-hidden="true" />{{
                        t(`${T}.LISTA.NOVA_SENHA`)
                      }}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      class="advup-menu__item"
                      @click="alternar(r)"
                    >
                      <i
                        :class="
                          r.ativo
                            ? 'i-hugeicons-toggle-off'
                            : 'i-hugeicons-toggle-on'
                        "
                        aria-hidden="true"
                      />
                      {{
                        r.ativo
                          ? t(`${T}.LISTA.DESLIGAR`)
                          : t(`${T}.LISTA.LIGAR`)
                      }}
                    </button>
                    <div class="advup-menu__filete" />
                    <button
                      type="button"
                      role="menuitem"
                      class="advup-menu__item advup-menu__item--perigo"
                      @click="pedirApagar(r)"
                    >
                      <i class="i-hugeicons-delete-02" aria-hidden="true" />{{
                        t(`${T}.LISTA.APAGAR`)
                      }}
                    </button>
                  </div>
                </Transition>
              </div>
            </div>

            <div class="tel-card__pessoa">
              <span
                class="advup-avatar"
                :class="
                  r.usuario
                    ? `advup-avatar--${corDoNome(r.usuario.nome)}`
                    : 'tel-card__sem-rosto'
                "
                aria-hidden="true"
              >
                <i v-if="!r.usuario" class="i-ph-user" />
                <template v-else>{{ iniciaisDoNome(r.usuario.nome) }}</template>
              </span>
              <span class="tel-card__quem">
                <span class="tel-card__nome">{{
                  r.usuario?.nome || t(`${T}.LISTA.SEM_PESSOA`)
                }}</span>
                <span v-if="r.nome" class="tel-card__visor">{{ r.nome }}</span>
              </span>
            </div>

            <div class="tel-card__pe">
              <span class="advup-etq" :class="{ 'advup-etq--rubi': r.gravar }">
                <i
                  :class="r.gravar ? 'i-ph-record-fill' : 'i-ph-record'"
                  aria-hidden="true"
                />
                <span>{{
                  r.gravar ? t(`${T}.LISTA.GRAVA`) : t(`${T}.LISTA.NAO_GRAVA`)
                }}</span>
              </span>
              <code class="tel-card__sip">{{ r.usuario_sip }}</code>
            </div>

            <button
              v-if="estadoDoRamal(r) === 'erro'"
              type="button"
              class="tel-card__reenviar"
              @click="reenviar(r)"
            >
              <i class="i-hugeicons-refresh" aria-hidden="true" />
              {{ t(`${T}.LISTA.REENVIAR`) }}
            </button>
          </li>
        </TransitionGroup>

        <p v-if="atualizadoEm" class="advup-nota tel-ramais__vivo">
          <span class="tel-ramais__pulso" aria-hidden="true" />
          {{
            t(`${T}.LISTA.ATUALIZADO`, { quando: dynamicTime(atualizadoEm) })
          }}
        </p>
      </section>

      <!-- ── A operadora e os números (etapa D) ──────────────────────────── -->
      <OperadoraSecao
        v-model:operadoras="operadoras"
        :ramais="ramais"
        :roteamento="roteamento"
        @ramais-mudaram="carregar({ silencioso: true })"
      />

      <!-- ── Filas, URAs, horários e o caminho de cada número (etapa E) ─── -->
      <RoteamentoSecao
        v-model:roteamento="roteamento"
        :ramais="ramais"
        :operadoras="operadoras"
      />
    </template>

    <RamalModal
      v-if="modal"
      :ramal="modal === 'novo' ? null : modal"
      :sugestao="proximoNumero(ramais)"
      :usuarios="usuarios"
      :numeros-usados="ramais.map(r => r.numero)"
      :salvando="salvando"
      :erro="erroDoModal"
      @close="modal = null"
      @salvar="salvar"
    />
    <CredenciaisModal
      v-if="credenciais"
      :credenciais="credenciais.credenciais"
      :ramal="credenciais.ramal"
      @close="credenciais = null"
    />
    <Dialog
      ref="dialogoApagar"
      type="alert"
      icone="i-ph-trash"
      :title="
        t(`${T}.MODAL.APAGAR_TIT`, { numero: confirmarApagar?.numero || '' })
      "
      :description="t(`${T}.MODAL.APAGAR_TXT`)"
      :confirm-button-label="t(`${T}.LISTA.APAGAR`)"
      :is-loading="ocupado"
      @confirm="apagar"
    />
    <Dialog
      ref="dialogoSenha"
      type="alert"
      icone="i-ph-key"
      :title="
        t(`${T}.MODAL.SENHA_TIT`, { numero: confirmarSenha?.numero || '' })
      "
      :description="t(`${T}.MODAL.SENHA_TXT`)"
      :confirm-button-label="t(`${T}.MODAL.SENHA_OK`)"
      :is-loading="ocupado"
      @confirm="gerarSenha"
    />
  </div>
</template>

<style scoped>
.tel {
  display: flex;
  flex-direction: column;
  gap: var(--esp-5);
  width: 100%;
}

/* ── A central ── */
.tel-central {
  --central-tom: rgb(var(--teal-9));
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--esp-4) var(--esp-5);
  padding: var(--esp-5) var(--esp-6);
  background-image: radial-gradient(
      circle at 0 0,
      color-mix(in srgb, var(--central-tom) 12%, transparent),
      transparent 55%
    ),
    linear-gradient(rgb(255 255 255 / 0.7) 0, rgb(255 255 255 / 0) 120px);
  animation: tel-sobe var(--mov-3) var(--mov-curva) backwards;
}

.dark .tel-central {
  background-image: radial-gradient(
    circle at 0 0,
    color-mix(in srgb, var(--central-tom) 16%, transparent),
    transparent 55%
  );
}

.tel-central--erro {
  --central-tom: rgb(var(--ruby-9));
}
.tel-central--nunca {
  --central-tom: rgb(var(--amber-9));
}
.tel-central--desligado {
  --central-tom: rgb(var(--slate-9));
}

.tel-central__orbe {
  position: relative;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: var(--r-redondo);
  background: var(--central-tom);
  color: rgb(255 255 255);
  font-size: var(--ico-4);
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 0.3),
    0 8px 18px -8px color-mix(in srgb, var(--central-tom) 80%, transparent);
}

/* A central no ar respira: um anel que abre e some (movimento ambiente). */

.tel-central__txt {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--esp-1);
  min-width: 220px;
}

.tel-central__rot {
  margin: 0;
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-rotulo);
  line-height: var(--linha-apertada);
  letter-spacing: var(--rastro-rotulo);
  text-transform: uppercase;
  color: rgb(var(--slate-11));
}

.tel-central__tit {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--txt-num-m);
  font-weight: var(--peso-titulo);
  line-height: var(--linha-apertada);
  letter-spacing: var(--rastro-titulo);
  color: color-mix(in srgb, var(--central-tom) 70%, rgb(var(--slate-12)));
}

.tel-central__sub {
  display: flex;
  flex-wrap: wrap;
  gap: var(--esp-1) var(--esp-3);
  margin: 0;
  font-size: var(--txt-nota);
  line-height: var(--linha-normal);
  color: rgb(var(--slate-11));
}

.tel-central__sub > span + span::before {
  content: '·';
  margin-right: var(--esp-3);
  color: rgb(var(--slate-9));
}

.tel-central__erro {
  margin: var(--esp-1) 0 0;
  font-size: var(--txt-nota);
  line-height: var(--linha-solta);
  color: rgb(var(--ruby-11));
}

/* ── Números ── */
.tel-numeros {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--esp-4);
}

.tel-numeros__online {
  animation-delay: var(--cascata-passo);
}
.tel-numeros__grava {
  animation-delay: calc(var(--cascata-passo) * 2);
}

/* ── Ramais ── */
.tel-ramais {
  display: flex;
  flex-direction: column;
  gap: var(--esp-3);
}

.tel-ramais__cab {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--esp-3);
}

.tel-ramais__tit {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  margin: 0;
  font-size: var(--txt-lista);
  font-weight: var(--peso-forte);
  line-height: var(--linha-apertada);
  color: rgb(var(--slate-12));
}

.tel-ramais__conta {
  min-width: 22px;
  padding: var(--esp-1) var(--esp-2);
  border-radius: var(--r-redondo);
  background: rgb(var(--slate-3));
  font-size: var(--txt-selo);
  font-weight: var(--peso-forte);
  line-height: var(--linha-apertada);
  text-align: center;
  color: rgb(var(--slate-11));
  font-variant-numeric: tabular-nums;
}

.tel-ramais__ferramentas {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--esp-2);
}

.tel-ramais__busca {
  width: 260px;
}

.tel-grade {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: var(--esp-4);
  margin: 0;
  padding: 0;
  list-style: none;
}

/* O cartão do ramal: o número em algarismo de mostrador, a luz do estado, quem
   usa. O véu no canto sai do tom do estado — teal no ar, cinza parado. */
.tel-card {
  --card-tom: rgb(var(--slate-9));
  display: flex;
  flex-direction: column;
  gap: var(--esp-4);
  padding: var(--esp-5);
  background-image: radial-gradient(
      circle at 100% 0,
      color-mix(in srgb, var(--card-tom) 10%, transparent),
      transparent 55%
    ),
    linear-gradient(rgb(255 255 255 / 0.7) 0, rgb(255 255 255 / 0) 120px);
  transition:
    transform var(--mov-2) var(--mov-curva),
    box-shadow var(--mov-2) var(--mov-curva),
    opacity var(--mov-2) var(--mov-curva);
  animation: tel-sobe var(--mov-3) var(--mov-curva) backwards;
  animation-delay: calc(var(--i, 0) * var(--cascata-passo));
}

.dark .tel-card {
  background-image: radial-gradient(
    circle at 100% 0,
    color-mix(in srgb, var(--card-tom) 14%, transparent),
    transparent 55%
  );
}

.tel-card:hover {
  transform: translateY(-2px);
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 0.7),
    0 2px 4px rgb(15 23 42 / 0.07),
    0 14px 30px -12px rgb(15 23 42 / 0.2);
}

.tel-card--online {
  --card-tom: rgb(var(--teal-9));
}
.tel-card--erro {
  --card-tom: rgb(var(--ruby-9));
}
.tel-card--desligado {
  opacity: 0.62;
}

.tel-card__topo {
  display: flex;
  align-items: flex-start;
  gap: var(--esp-2);
}

.tel-card__numero {
  font-family: var(--fonte-display);
  font-size: var(--txt-num-g);
  font-weight: var(--peso-num);
  line-height: var(--linha-num);
  letter-spacing: var(--rastro-rotulo);
  font-variant-numeric: tabular-nums;
  color: rgb(var(--slate-12));
}

.tel-card__estado {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  margin: var(--esp-1) 0 0 auto;
  font-size: var(--txt-nota);
  font-weight: var(--peso-dado);
  line-height: var(--linha-apertada);
  color: color-mix(in srgb, var(--card-tom) 75%, rgb(var(--slate-12)));
}

.tel-card--offline .tel-card__estado,
.tel-card--desligado .tel-card__estado {
  color: rgb(var(--slate-11));
}

.tel-card__luz {
  position: relative;
  width: 8px;
  height: 8px;
  border-radius: var(--r-redondo);
  background: var(--card-tom);
}

.tel-card--online .tel-card__luz {
  background: rgb(var(--ativo));
}

.tel-card--offline .tel-card__luz {
  background: rgb(var(--slate-7));
}

/* Online respira — o único movimento que não foi provocado: é o estado vivo. */

.tel-card__menu {
  position: relative;
}

.tel-card__opcoes {
  position: absolute;
  top: calc(100% + var(--esp-1));
  right: 0;
  z-index: 20;
  width: 220px;
}

.tel-card__opcoes .advup-menu__item {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  width: 100%;
  text-align: left;
}

.tel-card__opcoes .advup-menu__item > i {
  flex-shrink: 0;
  font-size: var(--ico-2);
}

.tel-card__pessoa {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  min-width: 0;
}

.tel-card__sem-rosto {
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-10));
}

.tel-card__quem {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.tel-card__nome {
  overflow: hidden;
  font-size: var(--txt-corpo);
  font-weight: var(--peso-forte);
  line-height: var(--linha-normal);
  white-space: nowrap;
  text-overflow: ellipsis;
  color: rgb(var(--slate-12));
}

.tel-card__visor {
  overflow: hidden;
  font-size: var(--txt-nota);
  line-height: var(--linha-normal);
  white-space: nowrap;
  text-overflow: ellipsis;
  color: rgb(var(--slate-11));
}

.tel-card__pe {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--esp-2);
  padding-top: var(--esp-3);
  border-top: 1px solid rgb(var(--slate-3));
}

.tel-card__sip {
  overflow: hidden;
  font-size: var(--txt-nota);
  line-height: var(--linha-apertada);
  white-space: nowrap;
  text-overflow: ellipsis;
  color: rgb(var(--slate-10));
}

.tel-card__reenviar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--esp-2);
  min-height: 36px;
  border: 0;
  border-radius: var(--r-3);
  background: rgb(var(--ruby-3));
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  line-height: var(--linha-apertada);
  color: rgb(var(--ruby-12));
  cursor: pointer;
  transition: background var(--mov-1) var(--mov-curva);
}

.tel-card__reenviar:hover {
  background: rgb(var(--ruby-4));
}

.tel-card__reenviar:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 2px;
}

.tel-ramais__vivo {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-2);
  margin: 0;
}

.tel-ramais__pulso {
  position: relative;
  width: 6px;
  height: 6px;
  border-radius: var(--r-redondo);
  background: rgb(var(--teal-9));
}

/* ── Movimento ── */
.tel-card-enter-active,
.tel-card-leave-active {
  transition:
    opacity var(--mov-2) var(--mov-curva),
    transform var(--mov-2) var(--mov-curva);
}

.tel-card-enter-from,
.tel-card-leave-to {
  opacity: 0;
  transform: scale(0.97);
}

.tel-pop-enter-active,
.tel-pop-leave-active {
  transition:
    opacity var(--mov-2) var(--mov-curva),
    transform var(--mov-2) var(--mov-curva);
}

.tel-pop-enter-from,
.tel-pop-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@keyframes tel-sobe {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .tel-central--ok .tel-central__orbe::after,
  .tel-card--online .tel-card__luz::after {
    animation: none;
  }
}

/* ── Celular ── */
@media (max-width: 767px) {
  .tel-numeros {
    grid-template-columns: minmax(0, 1fr);
  }

  .tel-central {
    padding: var(--esp-4);
  }

  .tel-central__testar {
    width: 100%;
    justify-content: center;
  }

  /* Em coluna, o separador "·" começaria a linha: some. */
  .tel-central__sub {
    flex-direction: column;
    gap: 0;
  }

  .tel-central__sub > span + span::before {
    content: none;
  }

  .tel-ramais__ferramentas,
  .tel-ramais__busca {
    width: 100%;
  }

  .tel-ramais__ferramentas > .advup-btn-forte {
    width: 100%;
    justify-content: center;
  }
}
</style>
